export type BackHandlerEntry = {
  id: string;
  label?: string;
  priority?: number;
  enabled?: boolean;
  handleBack: () => boolean;
};

type RegisteredBackHandler = Required<Pick<BackHandlerEntry, 'id' | 'handleBack'>> & {
  label: string;
  priority: number;
  enabled: boolean;
  order: number;
};

let nextOrder = 1;
const handlers = new Map<string, RegisteredBackHandler>();
const listeners = new Set<() => void>();

function notifyListeners() {
  listeners.forEach((listener) => listener());
}

export function registerBackHandler(entry: BackHandlerEntry) {
  const current = handlers.get(entry.id);
  handlers.set(entry.id, {
    id: entry.id,
    label: entry.label ?? entry.id,
    priority: entry.priority ?? 0,
    enabled: entry.enabled ?? true,
    handleBack: entry.handleBack,
    order: current?.order ?? nextOrder++,
  });
  notifyListeners();

  return () => {
    handlers.delete(entry.id);
    notifyListeners();
  };
}

export function updateBackHandler(entry: BackHandlerEntry) {
  const current = handlers.get(entry.id);
  if (!current) {
    return registerBackHandler(entry);
  }

  handlers.set(entry.id, {
    id: entry.id,
    label: entry.label ?? current.label,
    priority: entry.priority ?? current.priority,
    enabled: entry.enabled ?? current.enabled,
    handleBack: entry.handleBack,
    order: current.order,
  });
  notifyListeners();

  return () => {
    handlers.delete(entry.id);
    notifyListeners();
  };
}

export function dispatchRegisteredBack(): boolean {
  const activeHandlers = Array.from(handlers.values())
    .filter((handler) => handler.enabled)
    .sort((a, b) => b.priority - a.priority || b.order - a.order);

  for (const handler of activeHandlers) {
    try {
      if (handler.handleBack()) return true;
    } catch {
      // A broken handler should not trap the user in the app.
    }
  }

  return false;
}

export function hasRegisteredBackHandlers(): boolean {
  return Array.from(handlers.values()).some((handler) => handler.enabled);
}

export function subscribeBackRegistry(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

